import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, GraduationCap, Users } from 'lucide-react';
import CenterFilter from './CenterFilter';
import { courseAPI, getErrorMessage, registrationAPI } from '../services/api';

function Collapsible({ title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
          )}
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate">{title}</p>
            {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
          </div>
        </div>
      </button>
      {open ? <div className="border-t border-slate-100 px-4 py-3">{children}</div> : null}
    </div>
  );
}

export default function StudentRoster() {
  const [roster, setRoster] = useState({ total_students: 0, terms: [] });
  const [courses, setCourses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [centerId, setCenterId] = useState(null);
  const [courseId, setCourseId] = useState('');
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const loadFilters = async () => {
    try {
      const [coursesRes, termsRes] = await Promise.all([
        courseAPI.listCourses(),
        registrationAPI.getRosterTerms(),
      ]);
      setCourses(coursesRes.data.items || []);
      setTerms(termsRes.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const loadRoster = async () => {
    setLoading(true);
    try {
      const response = await registrationAPI.getRegisteredRoster({
        centerId: centerId || null,
        courseId: courseId || null,
        term: term || null,
      });
      setRoster(response.data || { total_students: 0, terms: [] });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadRoster();
  }, [centerId, courseId, term]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Registered Students</h1>
        <p className="text-gray-600 mt-1">
          Approved enrollments grouped by term, course, and center
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end bg-white rounded-lg shadow p-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
          <select
            className="border rounded-lg px-3 py-2 min-w-[180px]"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          >
            <option value="">All terms</option>
            {terms.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
          <select
            className="border rounded-lg px-3 py-2 min-w-[180px]"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
          >
            <option value="">All courses</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Center</label>
          <CenterFilter value={centerId} onChange={setCenterId} />
        </div>
      </div>

      <div className="flex items-center gap-2 text-slate-700">
        <Users className="h-5 w-5 text-blue-600" />
        <span className="font-medium">{roster.total_students} registered students</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-10 w-10 border-b-2 border-blue-500 rounded-full" />
        </div>
      ) : roster.terms.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">
          No registered students match your filters.
        </div>
      ) : (
        <div className="space-y-4">
          {roster.terms.map((termGroup) => (
            <Collapsible
              key={termGroup.term}
              title={termGroup.term}
              subtitle={`${termGroup.student_count} students`}
              defaultOpen
            >
              <div className="space-y-3">
                {termGroup.courses.map((courseGroup) => (
                  <Collapsible
                    key={`${termGroup.term}-${courseGroup.course_id || courseGroup.course_name}`}
                    title={courseGroup.course_name}
                    subtitle={`${courseGroup.student_count} students`}
                  >
                    <div className="space-y-3">
                      {courseGroup.centers.map((centerGroup) => (
                        <Collapsible
                          key={`${termGroup.term}-${courseGroup.course_name}-${centerGroup.center_name}`}
                          title={centerGroup.center_name}
                          subtitle={`${centerGroup.student_count} students`}
                        >
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-slate-500 border-b">
                                  <th className="py-2 pr-4 font-medium">Student</th>
                                  <th className="py-2 pr-4 font-medium">Grade</th>
                                  <th className="py-2 pr-4 font-medium">Parent</th>
                                  <th className="py-2 pr-4 font-medium">Phone</th>
                                  <th className="py-2 pr-4 font-medium">Fee</th>
                                  <th className="py-2 font-medium">Registered</th>
                                </tr>
                              </thead>
                              <tbody>
                                {centerGroup.students.map((student) => (
                                  <tr
                                    key={student.registration_id}
                                    className="border-b border-slate-100 last:border-0"
                                  >
                                    <td className="py-2 pr-4 font-medium text-slate-900">
                                      <span className="inline-flex items-center gap-1.5">
                                        <GraduationCap className="h-4 w-4 text-blue-500" />
                                        {student.child_name}
                                      </span>
                                    </td>
                                    <td className="py-2 pr-4 text-slate-600">
                                      {student.child_grade || '—'}
                                    </td>
                                    <td className="py-2 pr-4 text-slate-600">{student.parent_name}</td>
                                    <td className="py-2 pr-4 text-slate-600">
                                      {student.parent_phone || '—'}
                                    </td>
                                    <td className="py-2 pr-4 text-slate-600">
                                      {student.assigned_fee != null
                                        ? `${Number(student.assigned_fee).toFixed(2)} Birr`
                                        : '—'}
                                    </td>
                                    <td className="py-2 text-slate-600">
                                      {student.registered_at
                                        ? format(new Date(student.registered_at), 'MMM d, yyyy')
                                        : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Collapsible>
                      ))}
                    </div>
                  </Collapsible>
                ))}
              </div>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
}
